/**
 * F-17-marks / F-17-type rendering: turn a cell's text, its marks and its
 * line kinds into styled runs.
 *
 * Shared by the two surfaces that paint cell text — the grid cell (F-10) and
 * the script block's mirror layer (F-28) — so a phrase bolded in one view
 * looks the same in the other, and a sound cue is green in both.
 *
 * Every mark here is METRIC-NEUTRAL — it may change how a character looks but
 * never how wide it is. Two things depend on that: the script block's mirror
 * layer is a character-for-character match of a transparent textarea (caret,
 * native selection and wrap points all come from the textarea's metrics), and
 * the grid predicts cell extents from deterministic text measurement
 * (core/measure.ts) that only knows the base weight. Which is why `.tm-bold`
 * in app.css is a painted weight rather than `font-weight: 700`, and why a
 * line kind's italic and brackets — both of which DO change width — hang off
 * a `.tt-shown` ancestor that only a finished, not-being-edited cell wears.
 */
import {
  segmentMarkedText,
  type CellTextMarks,
  type TextSegment,
  type TextTypeSwitch,
} from "@kola/shared";
import { Fragment, type CSSProperties, type ReactNode } from "react";

import { readableTextColor, themedFill } from "../core/color.js";
import { textTypeBrackets, textTypeClassName, typeRunEdges } from "./text-type.js";

/** The class list for one run — nothing for an unformatted narration run. */
export function markClassName(segment: TextSegment): string | undefined {
  const classes: string[] = [];
  if (segment.bold === true) classes.push("tm-bold");
  if (segment.strike === true) classes.push("tm-strike");
  const type = textTypeClassName(segment.type);
  if (type !== undefined) classes.push(type);
  return classes.length > 0 ? classes.join(" ") : undefined;
}

/**
 * Inline colors for one run — user-chosen values, so never a class.
 *
 * A HIGHLIGHTED run with no explicit ink derives readable ink from the
 * highlight itself, exactly as F-17-color does for a whole-cell fill: without
 * it, a pale yellow highlight is near-white-on-pale-yellow — i.e. unreadable —
 * the moment the same board is opened in dark mode. An explicit ink color the
 * writer chose always wins; it is their call.
 */
export function markStyle(
  segment: TextSegment,
  surface: string,
  defaultText: string,
): CSSProperties | undefined {
  if (segment.color === undefined && segment.hl === undefined) return undefined;
  const style: CSSProperties = {};
  // Same F-122 re-rendering as a whole-cell fill: the stored highlight is a
  // light-mode pastel, and on a dark surface it becomes the dark version of
  // its own hue (core/color.ts `themedFill`). The ink is then derived from
  // what is actually painted.
  const hl = themedFill(segment.hl, surface);
  if (hl !== undefined) style.background = hl;
  const ink = segment.color ?? readableTextColor(hl, surface, defaultText);
  if (ink !== undefined) style.color = ink;
  return style;
}

/**
 * `text` with its marks and line kinds applied, each sound/visual run inside
 * drawn brackets. Unmarked narration renders as the bare string — no wrapper
 * span — because that is what almost every cell on a board is and a per-cell
 * span would be pure DOM weight.
 *
 * The brackets are always emitted here: this component paints FINISHED cells
 * only. The editors' mirror layers, which may not change the text's width,
 * walk `segmentMarkedText` themselves and leave the brackets out.
 */
export function MarkedText({
  text,
  marks,
  types,
  surface,
  defaultText,
}: {
  text: string;
  marks: CellTextMarks | undefined;
  /** F-17-type switch points; undefined = all narration. */
  types?: readonly TextTypeSwitch[] | undefined;
  /** `--color-surface` / `--color-text` of the reader's theme (F-122) — what
   *  the highlight's readable-ink derivation computes from. */
  surface: string;
  defaultText: string;
}): ReactNode {
  if ((marks === undefined && types === undefined) || text.length === 0) return text;
  const segments = segmentMarkedText(text, marks, types);
  if (segments.length === 1 && markClassName(segments[0]!) === undefined) {
    if (markStyle(segments[0]!, surface, defaultText) === undefined) return text;
  }
  return segments.map((segment, i) => {
    const { opens, closes } = typeRunEdges(segments, i);
    // `[ ]` for the built-ins, `‹ ›` for an admin-defined kind (text-type.ts).
    const [open, close] = textTypeBrackets(segment.type);
    return (
      <Fragment key={i}>
        {opens && <span className="tt-bracket">{open}</span>}
        <span className={markClassName(segment)} style={markStyle(segment, surface, defaultText)}>
          {segment.text}
        </span>
        {closes && <span className="tt-bracket">{close}</span>}
      </Fragment>
    );
  });
}
